/**
 * Zip Codes Module - TanStack Query Hooks
 *
 * This module provides hooks for fetching Mexican zip code information
 * from an external API service.
 *
 * @module useZipCodes
 */
import { useQuery } from "@tanstack/react-query";

import { getZipCode, type ZipCodeData } from "../api/zip-codes";
import { queryKeys } from "./queryKeys";

export type { ZipCodeData };

/**
 * Hook to fetch zip code information
 *
 * This hook queries an external API to get detailed information about
 * a Mexican zip code including municipality, state, city, and neighborhoods (colonias).
 *
 * @param zipCode - The zip code to query (5 digits)
 * @param options - Optional configuration
 * @param options.enabled - Whether the query should run (default: true)
 *
 * @returns TanStack Query result with zip code data
 *
 * @example
 * ```tsx
 * const { data, isLoading, error } = useGetZipCode("01000");
 *
 * if (isLoading) return <Spinner />;
 * if (error) return <div>Error loading zip code data</div>;
 *
 * return (
 *   <div>
 *     <p>Municipality: {data.municipio}</p>
 *     <p>State: {data.estado}</p>
 *     <p>City: {data.ciudad}</p>
 *   </div>
 * );
 * ```
 */
export const useGetZipCode = (
  zipCode: string,
  options?: { enabled?: boolean },
) => {
  return useQuery({
    queryKey: queryKeys.zipCodes.detail(zipCode),
    queryFn: () => getZipCode(zipCode),
    // Only fetch if zipCode is valid (5 digits) and enabled
    enabled: (options?.enabled ?? true) && /^\d{5}$/.test(zipCode),
    // Cache zip code data for a long time since it rarely changes
    staleTime: 24 * 60 * 60 * 1000, // 24 hours
    gcTime: 7 * 24 * 60 * 60 * 1000, // 7 days
    // Retry only once for external API calls
    retry: 1,
  });
};
